/**
 * <Court> — la cancha como objeto visual central del producto.
 *
 * Un solo componente SVG para todo lo que se dibuja "sobre la cancha":
 *
 *  - **formaciones** (`players`): las seis fichas con camiseta, rol, líbero
 *    marcado y capitán subrayado (como en el acta FIVB);
 *  - **grilla de zonas** (`zones`): cada zona pintada con un valor y un color,
 *    para distribución del armador, sideout por rotación o mapas de ataque;
 *  - **media cancha o cancha completa** (`sides` con uno o dos equipos);
 *  - **girada 180°** (`rotated`): el DT la ve desde su lado del banco.
 *
 * El dibujo respeta la cancha FIVB: piso arena, líneas blancas, zona libre
 * azul, zonas de ataque de 3 m. Escala por `width` (px) y nunca se sale del
 * contenedor (`max-width: 100%`), así que la misma pieza sirve en el teléfono
 * del dirigente y en la laptop de la mesa.
 *
 * Los colores salen de variables CSS (`--court-*`, `--home`, `--away`), así el
 * modo de contraste alto del banco la redibuja sin tocar este archivo. La
 * geometría vive en `lib/court.ts`, que tiene sus propios tests.
 */
import type { KeyboardEvent } from 'react';
import { useT } from '../i18n/use-t';
import type { MessageKey } from '../i18n/index';
import {
  COURT_W,
  FRONT_DEPTH,
  HALF_L,
  ZONES_READING_ORDER,
  courtLayout,
  courtRole,
  inkForBucket,
  playerSpot,
  zoneRect,
  type HalfOrientation,
} from '../lib/court';
import { heatBucket, heatVar } from '../lib/charts';

export type CourtTeam = 'home' | 'away';

export interface CourtPlayer {
  number: number;
  /** Rol del plantel (`setter`, `outside`, …); lo desconocido no se rotula. */
  role?: string;
  libero?: boolean;
  captain?: boolean;
  /** Resaltado del banco: el elegido (`picked`) y su pareja de la regla 15.6 (`paired`). */
  mark?: 'picked' | 'paired';
}

export interface CourtZoneValue {
  zone: number;
  /** Texto grande de la zona (`54%`, `12`). */
  value: string;
  /** Segunda línea, chica (`12 ataques`, `R1`). */
  sub?: string;
  /** Color de fondo de la zona (un `var(--heat-N)` o un color de evaluación). */
  fill?: string;
  /** Tinta sobre ese fondo. */
  ink?: 'dark' | 'light';
  /** Tooltip. */
  title?: string;
}

export interface CourtSide {
  team: CourtTeam;
  /** Rótulo del equipo en la zona libre (opcional). */
  name?: string;
  /** Índice 0..5 = posición P1..P6. `null` = posición vacía. */
  players?: ReadonlyArray<CourtPlayer | null>;
  zones?: ReadonlyArray<CourtZoneValue>;
  /** Saca este equipo: se dibuja la pelota junto a P1. */
  serving?: boolean;
  /** Prefijo del rótulo de cada zona (`Z` para zonas, `P` para posiciones). */
  zonePrefix?: string;
  /** Zona marcada (filtro activo). */
  selectedZone?: number | null;
  /** Con esto las zonas se vuelven botones. */
  onZone?: (zone: number) => void;
}

export interface CourtProps {
  /** Rótulo accesible del dibujo (empieza con «Cancha», no con «Matriz»). */
  label: string;
  /** Uno = media cancha; dos = cancha completa (el primero va ABAJO, cerca del que mira). */
  sides: readonly [CourtSide] | readonly [CourtSide, CourtSide];
  /** Girada 180°: el DT del otro lado. En cancha completa invierte qué equipo va abajo. */
  rotated?: boolean;
  /** Ancho en px. Sin esto ocupa el ancho del contenedor. */
  width?: number;
  /** Texto de la red (por defecto, el rótulo traducido). */
  netLabel?: string;
  className?: string;
}

/** Radio de la ficha de jugador, en unidades de cancha. */
const CHIP_R = 8.6;

export function Court({
  label,
  sides,
  rotated = false,
  width,
  netLabel,
  className,
}: CourtProps): JSX.Element {
  const t = useT();
  const full = sides.length === 2;
  const layout = courtLayout(full ? 2 : 1, rotated);

  // En cancha completa: la mitad de ARRIBA es la `down` (el rival visto desde
  // atrás de la propia). `sides[0]` es el de abajo salvo que esté girada.
  const ordered: Array<{ side: CourtSide; orientation: HalfOrientation; top: number }> = full
    ? (() => {
        const [near, far] = sides as readonly [CourtSide, CourtSide];
        const bottom = rotated ? far : near;
        const topSide = rotated ? near : far;
        return [
          { side: topSide, ...layout.halves[0]! },
          { side: bottom, ...layout.halves[1]! },
        ];
      })()
    : [{ side: sides[0], ...layout.halves[0]! }];

  const { x0 } = layout;
  const courtTop = ordered[0]!.top;
  const courtH = full ? 2 * HALF_L : HALF_L;
  const net = netLabel ?? t('court.net');

  return (
    <svg
      className={`court${className ? ` ${className}` : ''}`}
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      width={width ?? '100%'}
      role="img"
      aria-label={label}
      style={width !== undefined ? { maxWidth: '100%', height: 'auto' } : { height: 'auto' }}
    >
      <title>{label}</title>
      {/* Zona libre (azul FIVB) y piso. */}
      <rect className="court-free" x={0} y={0} width={layout.width} height={layout.height} rx={4} />
      <rect className="court-floor" x={x0} y={courtTop} width={COURT_W} height={courtH} />

      {ordered.map(({ side, orientation, top }, index) => (
        <HalfCourt key={index} side={side} orientation={orientation} x0={x0} top={top} t={t} />
      ))}

      {/* Líneas: perímetro, línea de ataque de cada mitad. */}
      <rect className="court-line" x={x0} y={courtTop} width={COURT_W} height={courtH} fill="none" />
      {ordered.map(({ orientation, top }, index) => {
        const y = orientation === 'up' ? top + FRONT_DEPTH : top + HALF_L - FRONT_DEPTH;
        return <line key={index} className="court-line" x1={x0} x2={x0 + COURT_W} y1={y} y2={y} />;
      })}

      {/* La red, con sus antenas: sin ella "arriba" no significa nada. */}
      <line className="court-net" x1={x0 - 6} x2={x0 + COURT_W + 6} y1={layout.netY} y2={layout.netY} />
      <line className="court-antenna" x1={x0} x2={x0} y1={layout.netY - 5} y2={layout.netY + 5} />
      <line
        className="court-antenna"
        x1={x0 + COURT_W}
        x2={x0 + COURT_W}
        y1={layout.netY - 5}
        y2={layout.netY + 5}
      />
      {/* Rótulo de la red: en media cancha, centrado en la zona libre del lado
          de la red; en la completa, en la zona libre lateral (adentro sería
          pisar la cancha). */}
      <text
        className="court-net-label"
        x={full ? 1.5 : layout.width / 2}
        y={full ? layout.netY - 2.5 : rotated ? layout.netY + 7 : layout.netY - 3.5}
        textAnchor={full ? 'start' : 'middle'}
      >
        {net}
      </text>

      {/* Nombre del equipo en la zona libre del fondo. */}
      {ordered.map(({ side, orientation, top }, index) =>
        side.name ? (
          <text
            key={index}
            className={`court-team-label ${side.team}`}
            // Del lado opuesto a Z1, que es donde se dibuja la pelota del saque.
            x={orientation === 'up' ? x0 : x0 + COURT_W}
            y={orientation === 'up' ? top + HALF_L + 12 : top - 6}
            textAnchor={orientation === 'up' ? 'start' : 'end'}
          >
            {side.name}
          </text>
        ) : null,
      )}
    </svg>
  );
}

function HalfCourt({
  side,
  orientation,
  x0,
  top,
  t,
}: {
  side: CourtSide;
  orientation: HalfOrientation;
  x0: number;
  top: number;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
}): JSX.Element {
  const zones = new Map((side.zones ?? []).map((z) => [z.zone, z]));
  const prefix = side.zonePrefix ?? 'Z';
  const hasZones = zones.size > 0;
  const hasPlayers = (side.players ?? []).some((p) => p !== null && p !== undefined);

  return (
    <g className={`court-half ${side.team}`}>
      {ZONES_READING_ORDER.map((zone) => {
        const rect = zoneRect(zone, orientation, x0, top);
        const value = zones.get(zone);
        const selected = side.selectedZone === zone;
        const clickable = side.onZone !== undefined;
        const onKey = (e: KeyboardEvent<SVGGElement>): void => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            side.onZone?.(zone);
          }
        };
        return (
          <g
            key={zone}
            className={`court-zone${selected ? ' selected' : ''}${clickable ? ' clickable' : ''}`}
            {...(clickable
              ? {
                  role: 'button',
                  tabIndex: 0,
                  'aria-pressed': selected,
                  'aria-label': `${prefix}${zone}${value ? ` · ${value.value}` : ''}`,
                  onClick: () => side.onZone?.(zone),
                  onKeyDown: onKey,
                }
              : {})}
          >
            {value?.title ? <title>{value.title}</title> : null}
            <rect
              className="court-zone-fill"
              x={rect.x}
              y={rect.y}
              width={rect.w}
              height={rect.h}
              style={value?.fill ? { fill: value.fill } : undefined}
            />
            <text
              className={`court-zone-label${value?.ink === 'light' ? ' light' : ''}`}
              x={rect.x + 2.5}
              y={rect.y + 6.5}
            >
              {`${prefix}${zone}`}
            </text>
            {value && !hasPlayers ? (
              <>
                <text
                  className={`court-zone-value${value.ink === 'light' ? ' light' : ''}`}
                  x={rect.x + rect.w / 2}
                  y={rect.y + rect.h / 2 + (value.sub ? 1 : 4)}
                  textAnchor="middle"
                >
                  {value.value}
                </text>
                {value.sub ? (
                  <text
                    className={`court-zone-sub${value.ink === 'light' ? ' light' : ''}`}
                    x={rect.x + rect.w / 2}
                    y={rect.y + rect.h / 2 + 9}
                    textAnchor="middle"
                  >
                    {value.sub}
                  </text>
                ) : null}
              </>
            ) : null}
            {selected ? (
              <rect
                className="court-zone-ring"
                x={rect.x + 1}
                y={rect.y + 1}
                width={rect.w - 2}
                height={rect.h - 2}
              />
            ) : null}
          </g>
        );
      })}

      {/* Separadores de zona: finos y punteados, para no competir con las líneas reales. */}
      {hasZones ? <ZoneGrid x0={x0} top={top} /> : null}

      {(side.players ?? []).map((player, index) => {
        if (player === null || player === undefined) return null;
        const position = index + 1;
        const spot = playerSpot(position, orientation, x0, top);
        const role = courtRole(player.libero ? 'libero' : player.role);
        return (
          <g
            key={position}
            className={`court-player ${side.team}${player.libero ? ' libero' : ''}${
              player.mark ? ` ${player.mark}` : ''
            }`}
          >
            <title>
              {`${t('court.playerTitle', { number: player.number, position })}${
                player.libero ? ` · ${t('court.libero')}` : ''
              }${player.captain ? ` · ${t('court.captain')}` : ''}`}
            </title>
            <circle className="court-player-chip" cx={spot.x} cy={spot.y} r={CHIP_R} />
            <text className="court-player-num" x={spot.x} y={spot.y + 3.6} textAnchor="middle">
              {player.number}
            </text>
            {player.captain ? (
              <line
                className="court-player-captain"
                x1={spot.x - 4}
                x2={spot.x + 4}
                y1={spot.y + 6}
                y2={spot.y + 6}
              />
            ) : null}
            {role !== null ? (
              <text
                className="court-player-role"
                x={spot.x}
                // Del lado que NO es la red: así nunca se pisa con ella.
                y={orientation === 'up' ? spot.y + CHIP_R + 5.5 : spot.y - CHIP_R - 2.2}
                textAnchor="middle"
              >
                {t(`court.roles.${role}` as MessageKey)}
              </text>
            ) : null}
          </g>
        );
      })}

      {side.serving ? <ServeBall orientation={orientation} x0={x0} top={top} label={t('court.serving')} /> : null}
    </g>
  );
}

function ZoneGrid({ x0, top }: { x0: number; top: number }): JSX.Element {
  const third = COURT_W / 3;
  return (
    <g className="court-grid">
      <line x1={x0 + third} x2={x0 + third} y1={top} y2={top + HALF_L} />
      <line x1={x0 + 2 * third} x2={x0 + 2 * third} y1={top} y2={top + HALF_L} />
    </g>
  );
}

/** La pelota del saque: detrás de la línea de fondo, del lado de Z1. */
function ServeBall({
  orientation,
  x0,
  top,
  label,
}: {
  orientation: HalfOrientation;
  x0: number;
  top: number;
  label: string;
}): JSX.Element {
  const z1 = zoneRect(1, orientation, x0, top);
  const cx = z1.x + z1.w / 2;
  const cy = orientation === 'up' ? top + HALF_L + 8 : top - 8;
  return (
    <g className="court-serve">
      <title>{label}</title>
      <circle cx={cx} cy={cy} r={4.5} />
      <path d={`M ${cx - 4.5} ${cy} Q ${cx} ${cy - 3} ${cx + 4.5} ${cy}`} />
      <path d={`M ${cx} ${cy - 4.5} Q ${cx + 2.5} ${cy} ${cx} ${cy + 4.5}`} />
    </g>
  );
}

/**
 * Color de una zona según su valor dentro de [min, max]: la misma rampa del
 * heatmap (`--heat-0`…`--heat-5`), con la tinta que se lee encima.
 */
export function heatTone(value: number, min: number, max: number): { fill: string; ink: 'dark' | 'light' } {
  const bucket = heatBucket(value, min, max);
  return { fill: heatVar(bucket), ink: inkForBucket(bucket) };
}

/**
 * Rotación `[P1..P6]` + plantel → fichas de la cancha. Las posiciones sin dato
 * quedan vacías (como el acta en papel), nunca inventadas.
 */
export function playersFromRotation(
  rotation: readonly number[],
  roster: ReadonlyArray<{ number: number; role?: string; libero?: boolean; isCaptain?: boolean }> = [],
  liberos: readonly number[] = [],
): Array<CourtPlayer | null> {
  const byNumber = new Map(roster.map((p) => [p.number, p]));
  const liberoSet = new Set(liberos);
  return [0, 1, 2, 3, 4, 5].map((index) => {
    const number = rotation[index];
    if (number === undefined) return null;
    const info = byNumber.get(number);
    const player: CourtPlayer = { number };
    if (info?.role !== undefined) player.role = info.role;
    if (info?.libero === true || liberoSet.has(number)) player.libero = true;
    if (info?.isCaptain === true) player.captain = true;
    return player;
  });
}
